import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import RightRail from "@/components/RightRail";
import {
  Home,
  Briefcase,
  MessageCircle,
  Users,
  Sparkles,
  BarChart3,
  LineChart,
  Activity,
  Target,
  TrendingUp,
  Calendar,
  Plus,
} from "lucide-react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import MobileNav from "@/components/MobileNav";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart as ReBarChart,
  Bar,
  ResponsiveContainer,
} from "recharts";

type DailyRow = {
  day: string; // ISO date (yyyy-mm-dd)
  event_type?: string | null;
  count?: number | null;
  // Fallback possible shapes
  total?: number | null;
};

type TotalsRow = {
  event_type: string;
  count: number;
};

type AreaRow = {
  day: string;
  [key: string]: number | string;
};

type AnalyticsMeSummary = {
  totalEvents: number | null;
  daily: DailyRow[];
};

function formatDate(d: string) {
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return d;
  }
}

export default function RoleDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // UI and data state
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<AnalyticsMeSummary>({ totalEvents: 0, daily: [] });
  const [error, setError] = useState<string | null>(null);
  const [showCreateChoice, setShowCreateChoice] = useState(false);

  // Derived role
  const role = (user?.profile_type || "Student").toLowerCase();

  // Load analytics for current user
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;

        const resp = await fetch("/api/analytics/me/summary", {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!resp.ok) {
          throw new Error("Failed to load analytics");
        }

        const payload = await resp.json();
        if (!mounted) return;

        const data: AnalyticsMeSummary = payload?.data || { totalEvents: 0, daily: [] };
        setSummary({
          totalEvents: typeof data.totalEvents === "number" ? data.totalEvents : 0,
          daily: Array.isArray(data.daily) ? data.daily : [],
        });
      } catch (e: any) {
        if (mounted) setError(e?.message || "Failed to load analytics");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Prepare chart data for daily totals (stack by event_type if provided)
  const { areaData, eventTypes, stackedKeys } = useMemo(() => {
    const dayMap: Record<string, Record<string, number>> = {};
    const typesSet = new Set<string>();

    // Aggregate by day and event_type if available, otherwise use single total key
    if (summary.daily && summary.daily.length) {
      // Try to see if event_type exists
      const hasEventType = summary.daily.some((r) => r.event_type);
      if (hasEventType) {
        summary.daily.forEach((r) => {
          const dayKey = r.day?.slice(0, 10) || "unknown";
          const typeKey = String(r.event_type || "other");
          typesSet.add(typeKey);
          if (!dayMap[dayKey]) dayMap[dayKey] = {};
          const value = typeof r.count === "number" ? r.count : typeof r.total === "number" ? r.total : 0;
          dayMap[dayKey][typeKey] = (dayMap[dayKey][typeKey] || 0) + value;
        });
      } else {
        summary.daily.forEach((r) => {
          const dayKey = r.day?.slice(0, 10) || "unknown";
          if (!dayMap[dayKey]) dayMap[dayKey] = {};
          const value = typeof r.count === "number" ? r.count : typeof r.total === "number" ? r.total : 0;
          dayMap[dayKey]["events"] = (dayMap[dayKey]["events"] || 0) + value;
          typesSet.add("events");
        });
      }
    }

    const keys = Array.from(typesSet);
    const areaData: AreaRow[] = Object.entries(dayMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, row]) => ({ day, ...(row as Record<string, number>) }));

    return { areaData, eventTypes: keys, stackedKeys: keys };
  }, [summary.daily]);

  // Compute totals by event_type for bar chart
  const totalsByType: TotalsRow[] = useMemo(() => {
    const map: Record<string, number> = {};
    if (summary.daily && summary.daily.length) {
      if (summary.daily.some((r) => r.event_type)) {
        summary.daily.forEach((r) => {
          const t = String(r.event_type || "other");
          const value = typeof r.count === "number" ? r.count : typeof r.total === "number" ? r.total : 0;
          map[t] = (map[t] || 0) + value;
        });
      } else {
        // only overall totals
        const total = summary.daily.reduce((acc, r) => {
          const value = typeof r.count === "number" ? r.count : typeof r.total === "number" ? r.total : 0;
          return acc + value;
        }, 0);
        map["events"] = total;
      }
    }
    return Object.entries(map).map(([event_type, count]) => ({ event_type, count }));
  }, [summary.daily]);

  const chartConfig = useMemo(() => {
    // Assign colors to up to 6 types
    const palette = [
      "hsl(221 83% 53%)", // blue
      "hsl(271 91% 65%)", // purple
      "hsl(174 72% 45%)", // teal
      "hsl(25 95% 53%)",  // orange
      "hsl(142 76% 36%)", // green
      "hsl(344 84% 43%)", // red
    ];
    const config: Record<string, any> = {};
    stackedKeys.forEach((k, i) => {
      config[k] = { label: k, color: palette[i % palette.length] };
    });
    return config;
  }, [stackedKeys]);

  // Role-specific headline and KPI suggestions
  const roleTitle = useMemo(() => {
    switch (role) {
      case "company":
        return "Company Analytics";
      case "university":
        return "University Analytics";
      case "professor":
        return "Professor Analytics";
      case "influencer":
        return "Influencer Analytics";
      case "investor":
      case "philanthropist":
        return "Investor/Philanthropist Analytics";
      case "employer":
        return "Employer Analytics";
      default:
        return "My Analytics";
    }
  }, [role]);

  const kpiCards = useMemo(() => {
    const total = summary.totalEvents || 0;
    // Lightweight derived KPIs to demonstrate structure
    const dailyTotal = areaData.reduce(
      (acc, r) =>
        acc +
        stackedKeys.reduce((s, k) => s + (Number((r as any)[k] ?? 0)), 0),
      0
    );
    const avgPerDay = areaData.length ? Math.round(dailyTotal / areaData.length) : 0;

    // Role-tailored KPI labelling
    if (role === "company" || role === "employer") {
      return [
        { icon: Target, label: "Total Engagement", value: total },
        { icon: Activity, label: "Avg Daily Events", value: avgPerDay },
        { icon: TrendingUp, label: "7-Day Momentum", value: Math.max(0, avgPerDay - Math.floor(avgPerDay * 0.2)) },
      ];
    }
    if (role === "university" || role === "professor") {
      return [
        { icon: Target, label: "Total Interactions", value: total },
        { icon: Calendar, label: "Days Tracked", value: areaData.length },
        { icon: TrendingUp, label: "Avg Daily Views", value: avgPerDay },
      ];
    }
    if (role === "influencer" || role === "investor" || role === "philanthropist") {
      return [
        { icon: Sparkles, label: "Total Events", value: total },
        { icon: BarChart3, label: "Avg Daily", value: avgPerDay },
        { icon: TrendingUp, label: "Momentum", value: Math.max(0, avgPerDay - Math.floor(avgPerDay * 0.3)) },
      ];
    }
    return [
      { icon: Target, label: "Total Events", value: total },
      { icon: LineChart, label: "Avg Daily", value: avgPerDay },
      { icon: TrendingUp, label: "Days Tracked", value: areaData.length },
    ];
  }, [role, summary.totalEvents, areaData, stackedKeys]);

  return (
    <div className="min-h-screen bg-gray-50 lg:pr-24">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 sm:h-20">
            {/* Logo - Left */}
            <div className="flex-shrink-0 mr-2 sm:mr-4">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-sans font-normal text-[#18316B] tracking-wide">Trestune</h1>
            </div>

            {/* Center: Title */}
            <div className="flex-1 max-w-2xl mx-auto">
              <div className="text-center">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{roleTitle}</h2>
                <p className="text-xs sm:text-sm text-gray-500">Role-based insights and engagement metrics</p>
              </div>
            </div>

            {/* Right: placeholder for spacing */}
            <div className="flex-shrink-0 lg:hidden" />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex">
        <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-20 lg:pb-6">
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center text-gray-600">
              Loading analytics...
            </div>
          ) : error ? (
            <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6 text-center text-red-600">
              {error}
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {kpiCards.map((kpi, idx) => (
                  <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-trestune-primary to-purple-500 flex items-center justify-center mr-3">
                      <kpi.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">{kpi.label}</div>
                      <div className="text-lg font-semibold text-gray-900">{kpi.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Area Chart - Daily */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-900">Daily Activity (last 14 days)</h3>
                    <span className="text-xs text-gray-500 flex items-center">
                      <LineChart className="h-4 w-4 mr-1" /> Time-series
                    </span>
                  </div>
                  <ChartContainer config={chartConfig}>
                    <ResponsiveContainer width="100%" height={260}>
                      <AreaChart data={areaData}>
                        <defs>
                          {stackedKeys.map((k) => (
                            <linearGradient key={k} id={`fill-${k}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={`var(--color-${k})`} stopOpacity={0.4} />
                              <stop offset="95%" stopColor={`var(--color-${k})`} stopOpacity={0.05} />
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(107,114,128,0.2)" />
                        <XAxis dataKey="day" tickFormatter={formatDate} />
                        <YAxis />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        {stackedKeys.map((k) => (
                          <Area
                            key={k}
                            type="monotone"
                            dataKey={k}
                            stackId="1"
                            stroke={`var(--color-${k})`}
                            fill={`url(#fill-${k})`}
                            strokeWidth={2}
                            dot={false}
                          />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </div>

                {/* Bar Chart - Totals by Event Type */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-900">Totals by Event Type</h3>
                    <span className="text-xs text-gray-500 flex items-center">
                      <BarChart3 className="h-4 w-4 mr-1" /> Distribution
                    </span>
                  </div>
                  <ChartContainer
                    config={
                      totalsByType.reduce((acc, r, idx) => {
                        const palette = [
                          "hsl(221 83% 53%)",
                          "hsl(271 91% 65%)",
                          "hsl(174 72% 45%)",
                          "hsl(25 95% 53%)",
                          "hsl(142 76% 36%)",
                          "hsl(344 84% 43%)",
                        ];
                        acc[r.event_type] = { label: r.event_type, color: palette[idx % palette.length] };
                        return acc;
                      }, {} as Record<string, any>)
                    }
                  >
                    <ResponsiveContainer width="100%" height={260}>
                      <ReBarChart data={totalsByType}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(107,114,128,0.2)" />
                        <XAxis dataKey="event_type" />
                        <YAxis />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Bar dataKey="count" fill="var(--color-count, hsl(221 83% 53%))" radius={[6, 6, 0, 0]} />
                      </ReBarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </div>
              </div>

              {/* Role-Specific Panels (illustrative) */}
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                {role === "company" || role === "employer" ? (
                  <>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Hiring Funnel</h4>
                      <p className="text-sm text-gray-600">Track impressions → views → applications → hires.</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Top Tags</h4>
                      <p className="text-sm text-gray-600">Popular skills and interests among your audience.</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Response SLAs</h4>
                      <p className="text-sm text-gray-600">Average response times to applicants and messages.</p>
                    </div>
                  </>
                ) : role === "university" || role === "professor" ? (
                  <>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Research Visibility</h4>
                      <p className="text-sm text-gray-600">Engagement with research interests and publications.</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Inbound Enquiries</h4>
                      <p className="text-sm text-gray-600">Trends in collaboration and enquiry volume.</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">MOUs Overview</h4>
                      <p className="text-sm text-gray-600">Summary of MOUs and connected privileges.</p>
                    </div>
                  </>
                ) : role === "influencer" ? (
                  <>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Reach</h4>
                      <p className="text-sm text-gray-600">Audience impressions across Home and Opportunities.</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">CTR</h4>
                      <p className="text-sm text-gray-600">Click-through rates on promoted content.</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Follower Growth</h4>
                      <p className="text-sm text-gray-600">Month-over-month growth trends.</p>
                    </div>
                  </>
                ) : role === "investor" || role === "philanthropist" ? (
                  <>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Inbound Requests</h4>
                      <p className="text-sm text-gray-600">Quality and quantity of incoming proposals.</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Engagement Quality</h4>
                      <p className="text-sm text-gray-600">Signals indicating strong opportunities.</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Top Focus Areas</h4>
                      <p className="text-sm text-gray-600">Where to allocate the next discovery time.</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Activity Summary</h4>
                      <p className="text-sm text-gray-600">High-level overview of your interactions.</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Opportunities Viewed</h4>
                      <p className="text-sm text-gray-600">Trends in discovery and application interest.</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Network Growth</h4>
                      <p className="text-sm text-gray-600">Associates and community engagement.</p>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </main>

        {/* Right Sidebar Navigation (shared) */}
        <RightRail />

        {/* Global Create Choice Drawer */}
        <Drawer open={showCreateChoice} onOpenChange={setShowCreateChoice}>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>Create</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 pb-4">
              <p className="text-sm text-gray-600 mb-3">What would you like to post?</p>
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={() => {
                    setShowCreateChoice(false);
                    navigate("/home?create=post");
                  }}
                  className="w-full p-4 border border-gray-200 rounded-lg hover:border-trestune-primary hover:bg-blue-50 text-left"
                >
                  <div className="font-medium text-gray-900">Post on Home</div>
                  <div className="text-xs text-gray-600">Share text and images with your network</div>
                </button>
                {(user?.profile_type || "").toLowerCase() !== "student" && (
                  <button
                    onClick={() => {
                      setShowCreateChoice(false);
                      navigate("/opportunities?create=opportunity");
                    }}
                    className="w-full p-4 border border-gray-200 rounded-lg hover:border-trestune-primary hover:bg-blue-50 text-left"
                  >
                    <div className="font-medium text-gray-900">Post an Opportunity</div>
                    <div className="text-xs text-gray-600">Create an internship, job, hackathon, scholarship, etc.</div>
                  </button>
                )}
              </div>
            </div>
            <DrawerFooter />
          </DrawerContent>
        </Drawer>

        {/* Mobile Bottom Navigation */}
        <MobileNav />
      </div>
    </div>
  );
}
